// On-disk per-group role table — Signal GroupsV2 parity.
//
// Mirrors iOS GroupRoleStore.swift exactly.
//
// Backed by IndexedDB (one object store "group_roles", keyed by
// `${groupId}/${userId}`). Per-group `{userId: RoleRecord}` mapping.
//
// The store enforces:
//   - Authorisation matrix: members cannot demote anyone; admins can
//     manage members and peer admins but not owners; the owner can do
//     anything.
//   - Monotonicity by `(targetUserId, ts)`: an out-of-order older event
//     is dropped with a staleTs error.
//   - Owner uniqueness: setting `newRole == "owner"` for one user
//     atomically demotes the prior owner to "admin" (mirrors the
//     transfer-ownership flow).
//   - Self-demote guard: an owner cannot demote themselves to "member"
//     without first transferring ownership. Admins CAN self-demote.
//
// `prevRoleClaim` is *advisory* — a mismatch is NOT a hard reject. This
// mirrors Signal's last-writer-wins semantics under quorum.
//
// The caller is responsible for HMAC verification — this module assumes
// the payload has already been authenticated by `RoleChangeCodec.decode`.

import type { GroupRole, RoleChangePayload } from "./GroupRole";

// ---------------------------------------------------------------------------
// RoleRecord
// ---------------------------------------------------------------------------

export interface RoleRecord {
  readonly groupId: string;
  readonly userId: string;
  readonly role: GroupRole;
  readonly lastChangeTs: number;
  readonly lastActorUserId: string;
  readonly observedAt: number; // Date.now() ms
}

// ---------------------------------------------------------------------------
// RoleStoreError (discriminated union, mirrors iOS enum)
// ---------------------------------------------------------------------------

export type RoleStoreError =
  | { kind: "unauthorized"; actor: string; actorRole: GroupRole; requestedAction: string }
  | { kind: "illegalTransition"; from: GroupRole; to: GroupRole }
  | { kind: "staleTs"; claimed: number; current: number }
  | { kind: "ownerCannotSelfDemote" };

// ---------------------------------------------------------------------------
// GroupRoleStore
// ---------------------------------------------------------------------------

const DB_NAME = "kaether_group_roles";
const DB_VERSION = 1;
const STORE_NAME = "group_roles";

/**
 * Composite key for a role record: `${groupId}\x1f${userId}`.
 * The Unit-Separator (0x1F) cannot appear in well-formed identifiers.
 */
function recordKey(groupId: string, userId: string): string {
  return `${groupId}\x1f${userId}`;
}

/**
 * Open (or create) the IndexedDB database. Returns a promise resolving
 * to an IDBDatabase instance. Callers should not cache this — they call
 * `openDb()` once per operation and the browser caches the connection.
 */
function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

/**
 * Read all `RoleRecord`s for a given group from IndexedDB.
 * Returns a `Map<userId, RoleRecord>`.
 */
async function readTable(
  db: IDBDatabase,
  groupId: string,
): Promise<Map<string, RoleRecord>> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readonly");
    const store = tx.objectStore(STORE_NAME);
    // We use a key-range prefix: all keys starting with `${groupId}\x1f`.
    const prefix = `${groupId}\x1f`;
    const range = IDBKeyRange.bound(prefix, prefix + "￿", false, false);
    const req = store.getAll(range);
    req.onsuccess = () => {
      const map = new Map<string, RoleRecord>();
      for (const rec of req.result as RoleRecord[]) {
        map.set(rec.userId, rec);
      }
      resolve(map);
    };
    req.onerror = () => reject(req.error);
  });
}

/**
 * Write (put) a single `RoleRecord` into IndexedDB.
 */
async function writeRecord(
  db: IDBDatabase,
  record: RoleRecord,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    const store = tx.objectStore(STORE_NAME);
    store.put(record, recordKey(record.groupId, record.userId));
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

/**
 * Write multiple `RoleRecord`s atomically (single transaction).
 */
async function writeRecords(
  db: IDBDatabase,
  records: RoleRecord[],
): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    const store = tx.objectStore(STORE_NAME);
    for (const rec of records) {
      store.put(rec, recordKey(rec.groupId, rec.userId));
    }
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

/**
 * Delete all records for a given group.
 */
async function deleteGroup(db: IDBDatabase, groupId: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    const store = tx.objectStore(STORE_NAME);
    const prefix = `${groupId}\x1f`;
    const range = IDBKeyRange.bound(prefix, prefix + "￿", false, false);
    const req = store.openKeyCursor(range);
    req.onsuccess = () => {
      const cursor = req.result;
      if (cursor) {
        cursor.delete();
        cursor.continue();
      }
    };
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

/**
 * Delete ALL records in the store (reset all groups).
 */
async function deleteAll(db: IDBDatabase): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    const store = tx.objectStore(STORE_NAME);
    store.clear();
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

// ---------------------------------------------------------------------------
// Authorisation matrix
// ---------------------------------------------------------------------------

/**
 * Returns a `RoleStoreError` if the action is not allowed, or `null` if
 * it is allowed.
 *
 * Authorisation rules (mirrors iOS exactly):
 *   - owner:  can do anything.
 *   - admin:  can promote member→admin, demote admin→member (peer admins),
 *             and remove member or admin. Cannot touch owners. Cannot mint
 *             a new owner.
 *   - member / removed: cannot change anyone's role.
 */
function authorise(
  actorRole: GroupRole,
  targetRole: GroupRole,
  newRole: GroupRole,
  actor: string,
): RoleStoreError | null {
  switch (actorRole) {
    case "owner":
      return null;
    case "admin":
      // Admins may not touch owners.
      if (targetRole === "owner") {
        return {
          kind: "unauthorized",
          actor,
          actorRole: "admin",
          requestedAction: "modify owner",
        };
      }
      // Admins cannot mint a new owner (no transfer-ownership).
      if (newRole === "owner") {
        return {
          kind: "unauthorized",
          actor,
          actorRole: "admin",
          requestedAction: "promote to owner",
        };
      }
      return null;
    case "member":
    case "removed":
      return {
        kind: "unauthorized",
        actor,
        actorRole,
        requestedAction: "change role",
      };
  }
}

// ---------------------------------------------------------------------------
// In-memory store for self-tests (avoids IndexedDB in test environments)
// ---------------------------------------------------------------------------

/**
 * An in-memory table backend — same shape as the IndexedDB backend but
 * backed by a plain Map. Used by `GroupRoleStore.createInMemory()` so
 * self-tests can run without IndexedDB.
 */
class InMemoryBackend {
  private readonly data = new Map<string, RoleRecord>();

  readTable(groupId: string): Map<string, RoleRecord> {
    const out = new Map<string, RoleRecord>();
    const prefix = `${groupId}\x1f`;
    for (const [k, v] of this.data) {
      if (k.startsWith(prefix)) out.set(v.userId, v);
    }
    return out;
  }

  writeRecords(records: RoleRecord[]): void {
    for (const rec of records) {
      this.data.set(recordKey(rec.groupId, rec.userId), rec);
    }
  }

  deleteGroup(groupId: string): void {
    const prefix = `${groupId}\x1f`;
    for (const k of Array.from(this.data.keys())) {
      if (k.startsWith(prefix)) this.data.delete(k);
    }
  }

  deleteAll(): void {
    this.data.clear();
  }
}

// ---------------------------------------------------------------------------
// GroupRoleStore (public API)
// ---------------------------------------------------------------------------

/** Result type mirrors Swift's `Result<T, E>`. */
export type StoreResult<T> =
  | { ok: true; value: T }
  | { ok: false; error: RoleStoreError };

export class GroupRoleStore {
  // ---- IndexedDB-backed singleton ----
  private static _shared: GroupRoleStore | null = null;
  static get shared(): GroupRoleStore {
    if (!GroupRoleStore._shared) {
      GroupRoleStore._shared = new GroupRoleStore(null);
    }
    return GroupRoleStore._shared;
  }

  /**
   * Creates an isolated in-memory instance, suitable for self-tests.
   * Does not touch IndexedDB at all.
   */
  static createInMemory(): GroupRoleStore {
    return new GroupRoleStore(new InMemoryBackend());
  }

  private readonly mem: InMemoryBackend | null;

  private constructor(mem: InMemoryBackend | null) {
    this.mem = mem;
  }

  // ---- read / write helpers ----

  private async readTableImpl(groupId: string): Promise<Map<string, RoleRecord>> {
    if (this.mem) return this.mem.readTable(groupId);
    const db = await openDb();
    return readTable(db, groupId);
  }

  private async writeRecordsImpl(records: RoleRecord[]): Promise<void> {
    if (this.mem) {
      this.mem.writeRecords(records);
      return;
    }
    const db = await openDb();
    await writeRecords(db, records);
  }

  private async writeRecordImpl(record: RoleRecord): Promise<void> {
    if (this.mem) {
      this.mem.writeRecords([record]);
      return;
    }
    const db = await openDb();
    await writeRecord(db, record);
  }

  // ---- Bootstrap (group creation) ----

  /**
   * Sets the initial owner record without going through the authorisation
   * matrix — used at group creation. Refuses if any role records already
   * exist for this group, so this can never silently overwrite a real role
   * table.
   */
  async bootstrap(
    groupId: string,
    ownerUserId: string,
    now: number = Date.now(),
  ): Promise<RoleRecord | null> {
    const table = await this.readTableImpl(groupId);
    if (table.size > 0) return null;
    const record: RoleRecord = {
      groupId,
      userId: ownerUserId,
      role: "owner",
      lastChangeTs: Math.floor(now / 1000),
      lastActorUserId: ownerUserId,
      observedAt: now,
    };
    await this.writeRecordImpl(record);
    return record;
  }

  // ---- Apply role change ----

  /**
   * Applies a role-change payload, enforcing the authorisation matrix and
   * monotonicity. Returns `{ ok: true, value: RoleRecord }` on success;
   * `{ ok: false, error: RoleStoreError }` for any store error.
   *
   * The caller is responsible for HMAC verification — this method assumes
   * the payload has already been authenticated by `RoleChangeCodec.decode`.
   */
  async apply(
    payload: RoleChangePayload,
    now: number = Date.now(),
  ): Promise<StoreResult<RoleRecord>> {
    const table = await this.readTableImpl(payload.groupId);
    const actorRole: GroupRole = table.get(payload.actorUserId)?.role ?? "member";
    // An actor with no record is treated as a non-member (member-equivalent).

    // 1. Self-demote guard: an owner may not silently drop to "member".
    //    They must transfer ownership; that path goes through the
    //    "newRole == 'owner'" branch on a *different* targetUserId
    //    and demotes the prior owner to "admin" as a side effect.
    if (
      payload.actorUserId === payload.targetUserId &&
      payload.newRole === "member" &&
      (table.get(payload.actorUserId)?.role ?? "member") === "owner"
    ) {
      return { ok: false, error: { kind: "ownerCannotSelfDemote" } };
    }

    // 2. Monotonicity: older event for the same target is dropped.
    const existing = table.get(payload.targetUserId);
    if (existing !== undefined && payload.ts < existing.lastChangeTs) {
      return {
        ok: false,
        error: {
          kind: "staleTs",
          claimed: payload.ts,
          current: existing.lastChangeTs,
        },
      };
    }

    // 3. Authorisation matrix.
    const targetRole: GroupRole = existing?.role ?? "member";
    const authErr = authorise(actorRole, targetRole, payload.newRole, payload.actorUserId);
    if (authErr) {
      return { ok: false, error: authErr };
    }

    // 4. Owner uniqueness: setting newRole == "owner" demotes the prior
    //    owner (if any, and if it isn't the same person) to "admin" —
    //    atomically with the new record.
    const toWrite: RoleRecord[] = [];
    if (payload.newRole === "owner") {
      for (const [uid, rec] of table) {
        if (rec.role === "owner" && uid !== payload.targetUserId) {
          toWrite.push({
            groupId: payload.groupId,
            userId: uid,
            role: "admin",
            lastChangeTs: payload.ts,
            lastActorUserId: payload.actorUserId,
            observedAt: now,
          });
        }
      }
    }

    const newRecord: RoleRecord = {
      groupId: payload.groupId,
      userId: payload.targetUserId,
      role: payload.newRole,
      lastChangeTs: payload.ts,
      lastActorUserId: payload.actorUserId,
      observedAt: now,
    };
    toWrite.push(newRecord);
    await this.writeRecordsImpl(toWrite);
    return { ok: true, value: newRecord };
  }

  // ---- Queries ----

  async currentRole(groupId: string, userId: string): Promise<GroupRole | null> {
    const table = await this.readTableImpl(groupId);
    return table.get(userId)?.role ?? null;
  }

  async record(groupId: string, userId: string): Promise<RoleRecord | null> {
    const table = await this.readTableImpl(groupId);
    return table.get(userId) ?? null;
  }

  /** Every userId in the group whose role is not "removed". */
  async members(groupId: string): Promise<RoleRecord[]> {
    const table = await this.readTableImpl(groupId);
    return Array.from(table.values()).filter((r) => r.role !== "removed");
  }

  /** Every userId previously kicked from the group (history). Ordered by lastChangeTs asc. */
  async removedUsers(groupId: string): Promise<string[]> {
    const table = await this.readTableImpl(groupId);
    return Array.from(table.values())
      .filter((r) => r.role === "removed")
      .sort((a, b) => a.lastChangeTs - b.lastChangeTs)
      .map((r) => r.userId);
  }

  // ---- Reset ----

  async clear(groupId: string): Promise<void> {
    if (this.mem) {
      this.mem.deleteGroup(groupId);
      return;
    }
    const db = await openDb();
    await deleteGroup(db, groupId);
  }

  async resetAll(): Promise<void> {
    if (this.mem) {
      this.mem.deleteAll();
      return;
    }
    const db = await openDb();
    await deleteAll(db);
  }
}
